import { fullPageRedirect } from '@hungryroot/environment';
import {
  loadQuizFromLocalStorage,
  clearQuizLocalStorage,
  deriveFunnelFavoritePairings,
  postFunnelFavorites,
  useServerPersist,
  type Quiz,
} from '@hungryroot/funnel';
import { log, logUnknownErrorResponse } from '@hungryroot/o11y';
import { type SignupResponse } from '@hungryroot/rest';
import {
  ROUTE_QUIZ_MEAL_FREQUENCIES,
  CYO_ROUTE,
  ROUTE_QUIZ_INTRO,
} from '@hungryroot/routes';

import {
  funnelAnalytics,
  segmentAnalytics as analytics,
} from '@hungryroot/segment';

// Best-effort: turn positive funnel pairing swipes into favorites, mirroring
// the post-checkout deck's heart (rate + favorite). An empty quiz posts
// nothing. Never throws — a failure here must not block the post-signup redirect.
async function favoriteFunnelPairings(quiz: Quiz): Promise<void> {
  const favorites = deriveFunnelFavoritePairings(quiz);

  try {
    const { derived, succeeded, failed } = await postFunnelFavorites(favorites);
    log('funnel_favorites', { derived, succeeded, failed });
  } catch (error) {
    logUnknownErrorResponse('Failed to post funnel favorites on signup', {
      key: 'useSignupSuccess.postFunnelFavorites',
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

export function useSignupSuccess() {
  const serverPersist = useServerPersist();

  return {
    onSuccess: (
      customerId: number,
      redirectUrl: string = CYO_ROUTE,
      signupResponse: SignupResponse,
      signupMethod: 'email' | 'facebook' | 'apple' | 'google',
      backRedirectUrl?: string
    ) => {
      const segment = funnelAnalytics();
      // Call analytics.identify immediatly after successful signup so userId is available for Completed Registration event.
      analytics.identify(`${customerId}`, {
        email: signupResponse.email,
        method: signupMethod,
      } as object);

      segment.completedRegistration(signupResponse, signupMethod);

      // this scenario could happen on intentional sign up or on any login page where a user believes
      // they have an account already
      const quiz = loadQuizFromLocalStorage();
      log('signup', {
        provider: signupMethod,
        quiz,
      });

      // if the user is redeeming a gift card, they can skip the quiz until after they finish redeeming
      const redirectingToRedeemGiftCard = redirectUrl.includes('redeem');
      if (redirectingToRedeemGiftCard) {
        return fullPageRedirect(redirectUrl);
      }

      // If there's no quiz data, fall back to the quiz intro.
      if (quiz === null) {
        log('quiz_null', {
          description: 'Signing up while quiz is null, reloading page',
        });
        return fullPageRedirect(ROUTE_QUIZ_INTRO);
      }

      serverPersist.mutate(
        {
          customerId,
          quiz,
        },
        {
          onSuccess: async () => {
            await favoriteFunnelPairings(quiz);

            clearQuizLocalStorage();
            // not sure what this logic is supposed to do -- putting the user on the sweets frequency page
            // and then be redirected to CYO? so they can press back and answer questions again?
            history.replaceState(
              undefined,
              document.title,
              backRedirectUrl || ROUTE_QUIZ_MEAL_FREQUENCIES
            );
            return fullPageRedirect(redirectUrl);
          },
          onError: (error, variables) => {
            logUnknownErrorResponse(
              'Failed to persist quiz data to server on signup',
              {
                key: 'quiz.serverPersistOnSignupV2',
                error: error instanceof Error ? error.message : String(error),
                customerId: variables.customerId,
              }
            );
          },
        }
      );
    },
  };
}
